// src/pages/CRM/SFA/QuoteList.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TablePagination, Button, IconButton, Chip,
  TextField, InputAdornment, FormControl, InputLabel, Select, MenuItem,
  Menu, Tooltip, CircularProgress, Alert, Grid, Dialog, DialogTitle,
  DialogContent, DialogActions, Stack, Avatar, Card, CardContent
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  MoreVert as MoreIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Description as QuoteIcon,
  Clear as ClearIcon,
  Send as SendIcon,
  PictureAsPdf as PdfIcon,
  ContentCopy as CopyIcon,
  CheckCircle as ApproveIcon,
  Drafts as DraftIcon,
  MarkEmailRead as SentIcon,
  ThumbUp as AcceptedIcon,
  Schedule as PendingIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { hasPermission } from '../../../../utils/permissionUtils';
import { quoteService } from './sfaService';
import {
  QuoteStatus, QuoteStatusLabels, QuoteStatusColors,
  formatCurrency
} from './sfaEnums';

const QuoteList = () => {
  const { chatbotId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { enqueueSnackbar } = useSnackbar();
  const opportunityId = searchParams.get('opportunity_id');

  // State
  const [loading, setLoading] = useState(true);
  const [quotes, setQuotes] = useState([]);
  const [pagination, setPagination] = useState({
    page: 0,
    per_page: 20,
    total: 0,
    pages: 0
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    status: '',
    opportunity_id: opportunityId || ''
  });

  // Permissions
  const [canView, setCanView] = useState(false);
  const [canModify, setCanModify] = useState(false);

  // Menu & Dialog state
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, quote: null });
  const [statusDialog, setStatusDialog] = useState({ open: false, quote: null, newStatus: '' });

  useEffect(() => {
    document.title = 'Quotes | Sales Force Automation';
    checkPermissions();
  }, []);

  useEffect(() => {
    if (canView) {
      loadQuotes();
    }
  }, [canView, pagination.page, pagination.per_page, searchTerm, filters]);

  const checkPermissions = async () => {
    try {
      const viewPerm = await hasPermission('crm', 'view');
      const modifyPerm = await hasPermission('crm', 'add|edit');
      setCanView(viewPerm);
      setCanModify(modifyPerm);
    } catch (err) {
      console.error('Error checking permissions:', err);
    }
  };

  const loadQuotes = async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.page + 1,
        per_page: pagination.per_page,
        search: searchTerm,
        ...filters
      };

      if (chatbotId) params.chatbot_id = chatbotId;
      Object.keys(params).forEach(key => !params[key] && delete params[key]);

      const response = await quoteService.getAll(params);
      if (response.success) {
        setQuotes(response.data);
        setPagination(prev => ({
          ...prev,
          total: response.pagination.total,
          pages: response.pagination.pages
        }));
      }
    } catch (err) {
      enqueueSnackbar('Failed to load quotes', { variant: 'error' });
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleChangeRowsPerPage = (event) => {
    setPagination(prev => ({
      ...prev,
      per_page: parseInt(event.target.value, 10),
      page: 0
    }));
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setPagination(prev => ({ ...prev, page: 0 }));
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPagination(prev => ({ ...prev, page: 0 }));
  };

  const clearFilters = () => {
    setFilters({ status: '', opportunity_id: '' });
    setSearchTerm('');
  };

  const handleMenuOpen = (event, quote) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setSelectedQuote(quote);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedQuote(null);
  };

  const handleDelete = async () => {
    if (!deleteDialog.quote) return;

    try {
      await quoteService.delete(deleteDialog.quote.id);
      enqueueSnackbar('Quote deleted', { variant: 'success' });
      setDeleteDialog({ open: false, quote: null });
      loadQuotes();
    } catch (err) {
      enqueueSnackbar('Failed to delete quote', { variant: 'error' });
    }
  };

  const handleStatusChange = async () => {
    if (!statusDialog.quote || !statusDialog.newStatus) return;

    try {
      await quoteService.updateStatus(statusDialog.quote.id, { status: statusDialog.newStatus });
      enqueueSnackbar('Quote status updated', { variant: 'success' });
      setStatusDialog({ open: false, quote: null, newStatus: '' });
      loadQuotes();
    } catch (err) {
      enqueueSnackbar('Failed to update status', { variant: 'error' });
    }
  };

  const handleSyncToOpportunity = async (quote) => {
    try {
      await quoteService.syncToOpportunity(quote.id);
      enqueueSnackbar('Quote synced to opportunity', { variant: 'success' });
      loadQuotes();
    } catch (err) {
      enqueueSnackbar('Failed to sync quote', { variant: 'error' });
    }
  };

  const getStatusChip = (status) => {
    return (
      <Chip
        label={QuoteStatusLabels[status] || status}
        size="small"
        color={QuoteStatusColors[status] || 'default'}
      />
    );
  };

  const isEditable = (status) => {
    return [QuoteStatus.DRAFT, QuoteStatus.REJECTED].includes(status);
  };

  // Calculate summary stats
  const draftCount = quotes.filter(q => q.status === QuoteStatus.DRAFT).length;
  const sentCount = quotes.filter(q => q.status === QuoteStatus.SENT).length;
  const acceptedCount = quotes.filter(q => q.status === QuoteStatus.ACCEPTED).length;
  const pendingCount = quotes.filter(q => q.status === QuoteStatus.PENDING_APPROVAL).length;

  if (!canView) {
    return (
      <Alert severity="error" sx={{ mt: { xs: 2, sm: 10 }, mx: 3 }}>
        You don't have permission to view quotes.
      </Alert>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 }, mt: { xs: 2, sm: 10 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={700} sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
            <QuoteIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Quotes
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Create and manage sales quotes
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Tooltip title="Refresh">
            <IconButton onClick={loadQuotes}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          {canModify && (
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => navigate(`/chatbot/${chatbotId}/crm/quotes/new${opportunityId ? `?opportunity_id=${opportunityId}` : ''}`)}
            >
              New Quote
            </Button>
          )}
        </Box>
      </Box>

      {/* Modern Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            elevation={0}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                    Total Quotes
                  </Typography>
                  <Typography variant="h3" fontWeight={700}>
                    {pagination.total}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.8 }}>
                    All time
                  </Typography>
                </Box>
                <Avatar
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.2)',
                    width: 56,
                    height: 56
                  }}
                >
                  <QuoteIcon fontSize="large" />
                </Avatar>
              </Box>
            </CardContent>
            <Box
              sx={{
                position: 'absolute',
                bottom: -20,
                right: -20,
                width: 100,
                height: 100,
                borderRadius: '50%',
                bgcolor: 'rgba(255,255,255,0.1)'
              }}
            />
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card
            elevation={0}
            sx={{
              background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
              color: 'white',
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                    Draft
                  </Typography>
                  <Typography variant="h3" fontWeight={700}>
                    {draftCount}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.8 }}>
                    Awaiting review
                  </Typography>
                </Box>
                <Avatar
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.2)',
                    width: 56,
                    height: 56
                  }}
                >
                  <DraftIcon fontSize="large" />
                </Avatar>
              </Box>
            </CardContent>
            <Box
              sx={{
                position: 'absolute',
                bottom: -20,
                right: -20,
                width: 100,
                height: 100,
                borderRadius: '50%',
                bgcolor: 'rgba(255,255,255,0.1)'
              }}
            />
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card
            elevation={0}
            sx={{
              background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
              color: 'white',
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                    Sent
                  </Typography>
                  <Typography variant="h3" fontWeight={700}>
                    {sentCount}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.8 }}>
                    Awaiting response
                  </Typography>
                </Box>
                <Avatar
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.2)',
                    width: 56,
                    height: 56
                  }}
                >
                  <SentIcon fontSize="large" />
                </Avatar>
              </Box>
            </CardContent>
            <Box
              sx={{
                position: 'absolute',
                bottom: -20,
                right: -20,
                width: 100,
                height: 100,
                borderRadius: '50%',
                bgcolor: 'rgba(255,255,255,0.1)'
              }}
            />
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card
            elevation={0}
            sx={{
              background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
              color: 'white',
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                    Accepted
                  </Typography>
                  <Typography variant="h3" fontWeight={700}>
                    {acceptedCount}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.8 }}>
                    Won deals
                  </Typography>
                </Box>
                <Avatar
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.2)',
                    width: 56,
                    height: 56
                  }}
                >
                  <AcceptedIcon fontSize="large" />
                </Avatar>
              </Box>
            </CardContent>
            <Box
              sx={{
                position: 'absolute',
                bottom: -20,
                right: -20,
                width: 100,
                height: 100,
                borderRadius: '50%',
                bgcolor: 'rgba(255,255,255,0.1)'
              }}
            />
          </Card>
        </Grid>
      </Grid>

      {/* Search & Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search quotes..."
              value={searchTerm}
              onChange={handleSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status}
                label="Status"
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                {Object.entries(QuoteStatusLabels).map(([key, label]) => (
                  <MenuItem key={key} value={key}>{label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            {(searchTerm || filters.status) && (
              <Button startIcon={<ClearIcon />} onClick={clearFilters}>
                Clear Filters
              </Button>
            )}
          </Grid>
        </Grid>
      </Paper>

      {/* Table */}
      <Paper>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table sx={{ minWidth: 700 }}>
            <TableHead>
              <TableRow>
                <TableCell>Quote</TableCell>
                <TableCell>Account</TableCell>
                <TableCell>Opportunity</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Total</TableCell>
                <TableCell>Valid Until</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : quotes.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                    <QuoteIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                    <Typography color="text.secondary">No quotes found</Typography>
                    {canModify && (
                      <Button
                        sx={{ mt: 2 }}
                        startIcon={<AddIcon />}
                        onClick={() => navigate(`/chatbot/${chatbotId}/crm/quotes/new`)}
                      >
                        Create First Quote
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ) : (
                quotes.map((quote) => (
                  <TableRow
                    key={quote.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/chatbot/${chatbotId}/crm/quotes/${quote.id}`)}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar
                          variant="rounded"
                          sx={{ bgcolor: 'primary.light', width: 40, height: 40 }}
                        >
                          <QuoteIcon />
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle2" fontWeight={600}>
                            {quote.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {quote.quote_number}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {quote.account?.name || '-'}
                    </TableCell>
                    <TableCell>
                      {quote.opportunity_id ? (
                        <Typography variant="body2" color="primary">
                          View Opportunity
                        </Typography>
                      ) : '-'}
                    </TableCell>
                    <TableCell>
                      {getStatusChip(quote.status)}
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight={600}>
                        {formatCurrency(quote.total_amount, quote.currency)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {quote.line_item_count} items
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {quote.expiration_date ? (
                        <Typography
                          variant="body2"
                          color={new Date(quote.expiration_date) < new Date() ? 'error' : 'text.primary'}
                        >
                          {new Date(quote.expiration_date).toLocaleDateString()}
                        </Typography>
                      ) : '-'}
                    </TableCell>
                    <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                      <IconButton size="small" onClick={(e) => handleMenuOpen(e, quote)}>
                        <MoreIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={pagination.total}
          page={pagination.page}
          onPageChange={handleChangePage}
          rowsPerPage={pagination.per_page}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 20, 50, 100]}
        />
      </Paper>

      {/* Context Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        {isEditable(selectedQuote?.status) && (
          <MenuItem onClick={() => {
            navigate(`/chatbot/${chatbotId}/crm/quotes/${selectedQuote?.id}/edit`);
            handleMenuClose();
          }}>
            <EditIcon fontSize="small" sx={{ mr: 1 }} />
            Edit
          </MenuItem>
        )}
        <MenuItem onClick={() => {
          navigate(`/chatbot/${chatbotId}/crm/quotes/${selectedQuote?.id}`);
          handleMenuClose();
        }}>
          <QuoteIcon fontSize="small" sx={{ mr: 1 }} />
          View Details
        </MenuItem>
        {selectedQuote?.status === QuoteStatus.DRAFT && canModify && (
          <MenuItem onClick={() => {
            setStatusDialog({ open: true, quote: selectedQuote, newStatus: QuoteStatus.SENT });
            handleMenuClose();
          }}>
            <SendIcon fontSize="small" sx={{ mr: 1 }} />
            Mark as Sent
          </MenuItem>
        )}
        {selectedQuote?.status === QuoteStatus.SENT && canModify && (
          <>
            <MenuItem onClick={() => {
              setStatusDialog({ open: true, quote: selectedQuote, newStatus: QuoteStatus.ACCEPTED });
              handleMenuClose();
            }}>
              <ApproveIcon fontSize="small" sx={{ mr: 1, color: 'success.main' }} />
              Mark as Accepted
            </MenuItem>
            <MenuItem onClick={() => {
              setStatusDialog({ open: true, quote: selectedQuote, newStatus: QuoteStatus.REJECTED });
              handleMenuClose();
            }}>
              <DeleteIcon fontSize="small" sx={{ mr: 1, color: 'error.main' }} />
              Mark as Rejected
            </MenuItem>
          </>
        )}
        {selectedQuote?.opportunity_id && canModify && (
          <MenuItem onClick={() => {
            handleSyncToOpportunity(selectedQuote);
            handleMenuClose();
          }}>
            <CopyIcon fontSize="small" sx={{ mr: 1 }} />
            Sync to Opportunity
          </MenuItem>
        )}
        {isEditable(selectedQuote?.status) && canModify && (
          <MenuItem
            onClick={() => {
              setDeleteDialog({ open: true, quote: selectedQuote });
              handleMenuClose();
            }}
            sx={{ color: 'error.main' }}
          >
            <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
            Delete
          </MenuItem>
        )}
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, quote: null })}>
        <DialogTitle>Delete Quote</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{deleteDialog.quote?.name}"?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, quote: null })}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={handleDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Status Change Confirmation Dialog */}
      <Dialog open={statusDialog.open} onClose={() => setStatusDialog({ open: false, quote: null, newStatus: '' })}>
        <DialogTitle>Update Quote Status</DialogTitle>
        <DialogContent>
          <Typography>
            Change status of "{statusDialog.quote?.name}" to {QuoteStatusLabels[statusDialog.newStatus]}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusDialog({ open: false, quote: null, newStatus: '' })}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleStatusChange}>
            Update Status
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default QuoteList;